/**
 * PhotoThumbnail - Square image tile with an optional "expand to
 * fullscreen" overlay button.
 *
 * The thumbnail is the primary atom for showing user-captured photos
 * inline (checklist rows, verification grids, photo galleries). It is
 * intentionally small in scope: a square, cropped image and an
 * optional overlay button that signals "tap to see this larger." The
 * actual fullscreen presentation is the consumer's responsibility —
 * pair this with `<PhotoLightbox>` (or any modal) when you want the
 * tap-to-expand affordance to do something.
 *
 * Sizing is configurable via the `size` prop so callers can fit the
 * thumbnail to context (a tight checklist row vs. a generous
 * gallery). Sizes use the project's px-based Tailwind classes per
 * the brand spacing convention.
 *
 * The expand button covers the whole tile so the entire image is
 * tappable. We stop click propagation inside the button so this atom
 * can sit safely inside larger interactive containers (e.g. the
 * checklist row, which has its own toggle button as a sibling — never
 * an ancestor, since nesting <button>s is invalid HTML).
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Allowed sizes for the thumbnail tile. */
export type PhotoThumbnailSize = 'sm' | 'md' | 'lg';

export interface PhotoThumbnailProps {
    /** Absolute URL or data: URL for the image. */
    src: string;
    /** Alt text — falls back to a generic "Captured photo" when omitted. */
    alt?: string;
    /** Tile size. Defaults to 'md' (72×72). */
    size?: PhotoThumbnailSize;
    /**
     * Optional handler invoked when the user taps the expand overlay.
     * When omitted the overlay is not rendered (the thumbnail
     * becomes a passive image with no interactive surface).
     */
    onExpand?: () => void;
    /** Optional className appended to the outer wrapper for layout tweaks. */
    className?: string;
}

/* ============================================
 * SIZE TOKENS
 * ============================================ */

/* Maps the size prop to concrete Tailwind classes. Centralized here
 * so consumers don't reach into our internals to size a tile. */
const SIZE_CLASSES: Record<PhotoThumbnailSize, string> = {
    sm: 'w-56px h-56px',
    md: 'w-72px h-72px',
    lg: 'w-96px h-96px',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const PhotoThumbnail: React.FC<PhotoThumbnailProps> = ({
    src,
    alt,
    size = 'md',
    onExpand,
    className,
}) => {
    return (
        <div
            data-ref="atom-photo-thumbnail"
            className={[
                'photo-thumbnail',
                'relative rounded-md overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0',
                SIZE_CLASSES[size],
                className ?? '',
            ].join(' ')}
        >
            {/* The image itself — `object-cover` keeps the aspect
             *  square without distorting the source. */}
            <img
                src={src}
                alt={alt ?? 'Captured photo'}
                className="photo-thumbnail__image w-full h-full object-cover"
            />

            {/* Expand overlay — covers the full tile so the entire
             *  thumbnail is tappable. The badge in the top-right
             *  signals "expand" even when the hover backdrop is
             *  invisible at rest. We stop click propagation so this
             *  atom can sit inside larger containers without
             *  triggering their click handlers. */}
            {onExpand && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onExpand();
                    }}
                    className="photo-thumbnail__expand absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 active:bg-black/50 transition-colors"
                    aria-label="Expand photo to full screen"
                    title="Expand photo"
                >
                    <span className="absolute top-2px right-2px w-20px h-20px rounded bg-black/60 text-white flex items-center justify-center">
                        {/* Heroicons "arrows-pointing-out" — reads
                         *  unambiguously as "expand" on tiny tiles. */}
                        <svg className="w-12px h-12px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                        </svg>
                    </span>
                </button>
            )}
        </div>
    );
};

export default PhotoThumbnail;
